import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { HomeSEO } from '../components/SEO';
import { 
  ArrowRight, CheckCircle, Clock, FileText, TrendingUp, Shield, 
  ChevronDown, ChevronUp, Zap, Star, PenTool, Mic, Quote,
  Newspaper, Globe, BarChart3, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const endValue = parseInt(end) || 0;
    
    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{count}{suffix}
    </span>
  );
};

const Home = () => {
  const [settings, setSettings] = useState({});
  const [metrics, setMetrics] = useState({});
  const [featuredPubs, setFeaturedPubs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Handle scroll for sticky CTA bar
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = 600; // Approximate hero section height
      setShowStickyBar(scrollY > heroHeight);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchLandingData();
  }, []);

  const fetchLandingData = async () => {
    try {
      const [settingsRes, metricsRes, pubsRes, testimonialsRes, faqsRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/metrics`),
        axios.get(`${API}/featured-publications`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/faqs`)
      ]);
      
      setSettings(settingsRes.data);
      setMetrics(metricsRes.data);
      setFeaturedPubs(pubsRes.data);
      setTestimonials(testimonialsRes.data);
      setFaqs(faqsRes.data);
    } catch (error) {
      console.error('Error fetching landing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pricingTiers = [
    {
      name: 'Express',
      tat: '48 Hours',
      description: 'For breaking news & time-critical announcements',
      icon: Zap,
      color: 'text-black',
      bgColor: 'bg-black',
      borderColor: 'border-black',
      highlight: true
    },
    {
      name: 'Fast',
      tat: '3-5 Working Days',
      description: 'Ideal for product launches & campaign stories',
      icon: Clock,
      color: 'text-gray-700',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      popular: true
    },
    {
      name: 'Standard',
      tat: '7-10 Working Days',
      description: 'Best value for evergreen content & thought leadership',
      icon: Star,
      color: 'text-gray-700',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200'
    }
  ];

  const writingServices = [
    {
      name: 'Standard',
      price: '₹2,500',
      description: '600-800 words, journalistic tone',
      features: ['Publication-ready article', '1 revision round', 'Guideline compliance']
    },
    {
      name: 'Premium',
      price: '₹4,000',
      description: 'Editorial quality, thought leadership',
      features: ['In-depth coverage', 'SEO optimized', 'Expert positioning']
    },
    {
      name: 'Interview',
      price: '₹6,000',
      description: 'Q&A format with professional writer',
      features: ['30-min interview call', 'Authentic voice', 'Multiple quotes']
    }
  ];

  // Static FAQs covering all services
  const staticFaqs = [
    {
      id: 1,
      question: 'What services does BusinessMint PR offer?',
      answer: 'We offer three types of media services: Digital PR (online publication placements with high DA backlinks), Social Media Marketing (promotions through verified influencers on Instagram, YouTube, Telegram), and Combo Bundles (curated packages combining multiple services at discounted rates up to 35% off).'
    },
    {
      id: 2,
      question: 'How does the ordering process work?',
      answer: 'Simply browse our marketplace, select your desired service (publication, influencer, print outlet, or bundle), add to cart, and complete checkout. After payment, submit your content/requirements through your dashboard. Track real-time status until delivery.'
    },
    {
      id: 3,
      question: 'What is the turnaround time for each service?',
      answer: 'Digital PR: Express (48h), Fast (3-5 days), Standard (7-10 days). Social Media: Most posts go live within 24-48 hours. Bundles: Coordinated delivery based on included services.'
    },
    {
      id: 4,
      question: 'Do you offer refunds?',
      answer: 'Yes! We offer a 100% refund guarantee. If your Digital PR article is rejected, social post doesn\'t go live, or print coverage fails to publish, you receive a full refund. No questions asked.'
    },
    {
      id: 5,
      question: 'Do I need to provide content?',
      answer: 'For Digital PR, you can provide your own article or use our professional writing service. For Social Media, we typically need your brand assets/creative or can create content based on your brief.'
    },
    {
      id: 6,
      question: 'How do bundles work and how much can I save?',
      answer: 'Bundles combine multiple services (e.g., 3 publications, or PR + Social combo) at discounted rates. Savings range from 15% to 35% compared to individual purchases. All services in a bundle are coordinated for maximum impact and tracked from a single dashboard.'
    }
  ];

  // Use database FAQs if available, otherwise use static FAQs
  const displayFaqs = faqs.length > 0 ? faqs : staticFaqs;

  return (
    <div className="min-h-screen bg-white">
      <HomeSEO />
      <Header />
      
      {/* ========== HERO SECTION - POWERHOUSE ========== */}
      <section className="relative overflow-hidden bg-[#0d0d0d] min-h-[90vh] flex items-center">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #D4AF37 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #D4AF37 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        {/* Gold Accent Lines */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
        
        {/* Floating Gold Orbs */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#D4AF37]/5 rounded-full blur-2xl animate-float-delayed"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left - Main Content */}
            <div className="lg:col-span-7 stagger-children">
              {/* Badge Row */}
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-manrope uppercase tracking-widest bg-[#D4AF37]/5">
                  <Newspaper className="w-4 h-4" />
                  India&apos;s Premium PR Platform
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0d0d0d] text-xs font-manrope uppercase tracking-widest font-bold animate-pulse-ring">
                  <Zap className="w-3 h-3 fill-current" />
                  48-Hour Express
                </div>
              </div>
              
              {/* Main Headline */}
              <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] mb-6 text-white">
                Your Brand.{' '}
                <span className="text-gold-gradient">Every</span>{' '}
                <span className="relative inline-block">
                  Platform.
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-[#D4AF37]"></span>
                </span>
              </h1>
              
              {/* Subheadline */}
              <p className="font-manrope text-lg md:text-xl text-gray-400 mb-10 max-w-2xl leading-relaxed">
                {settings.homepage_subheadline || 'Complete PR & media solutions. Digital PR, social media promotions, and combo bundles - all in one platform.'}
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link
                  to="/marketplace"
                  data-testid="hero-browse-btn"
                  className="btn-shimmer inline-flex items-center justify-center gap-3 px-10 py-5 font-manrope text-sm uppercase tracking-widest"
                >
                  Explore Services
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="#how-it-works"
                  data-testid="hero-how-it-works-btn"
                  className="inline-flex items-center justify-center gap-3 px-10 py-5 border-2 border-[#D4AF37]/50 text-white font-manrope text-sm uppercase tracking-widest hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-300"
                >
                  See How It Works
                </a>
              </div>
              
              {/* Trust Metrics - Horizontal */}
              <div className="flex flex-wrap items-center gap-8 pt-8 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="font-mono text-2xl font-bold text-white">
                      <AnimatedCounter end={200} suffix="+" />
                    </div>
                    <div className="font-manrope text-xs text-gray-500 uppercase tracking-wider">Media Partners</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="font-mono text-2xl font-bold text-white">
                      <AnimatedCounter end={metrics.total_orders_completed || 500} suffix="+" />
                    </div>
                    <div className="font-manrope text-xs text-gray-500 uppercase tracking-wider">Articles Live</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="font-mono text-2xl font-bold text-white">100%</div>
                    <div className="font-manrope text-xs text-gray-500 uppercase tracking-wider">Refund Guarantee</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right - Featured Card */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                {/* Main Feature Card */}
                <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] border border-[#D4AF37]/20 p-8 animate-border-glow">
                  {/* Live Badge */}
                  <div className="absolute -top-3 -right-3 bg-[#D4AF37] text-[#0d0d0d] px-4 py-1.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#0d0d0d] rounded-full animate-pulse"></span>
                    Live Coverage
                  </div>
                  
                  {/* Publication Preview */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center">
                        <span className="font-playfair text-2xl font-bold text-[#0d0d0d]">ET</span>
                      </div>
                      <div>
                        <div className="font-playfair text-xl font-bold text-white">Economic Times</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-mono">DA 92</span>
                          <span className="text-gray-500 text-xs">•</span>
                          <span className="text-gray-400 text-xs">Business</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#0d0d0d] p-4 border border-[#D4AF37]/10">
                        <div className="font-mono text-2xl font-bold text-[#D4AF37]">4.2M</div>
                        <div className="text-xs text-gray-500 mt-1">Monthly Visitors</div>
                      </div>
                      <div className="bg-[#0d0d0d] p-4 border border-[#D4AF37]/10">
                        <div className="font-mono text-2xl font-bold text-white">48h</div>
                        <div className="text-xs text-gray-500 mt-1">Express TAT</div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-gray-400">Order Progress</span>
                        <span className="text-[#D4AF37] font-bold">Published</span>
                      </div>
                      <div className="h-2 bg-[#0d0d0d] overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F4E4BA] w-full"></div>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <Link
                      to="/marketplace"
                      className="block w-full py-4 bg-[#D4AF37] text-[#0d0d0d] text-center font-manrope text-sm uppercase tracking-widest font-bold hover:bg-[#B8960F] transition-colors"
                    >
                      Explore All Services
                    </Link>
                  </div>
                </div>
                
                {/* Floating Cards */}
                <div className="absolute -bottom-6 -left-6 bg-[#0d0d0d] border border-[#D4AF37]/30 p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-[#0d0d0d]" />
                    </div>
                    <div>
                      <div className="font-mono text-lg font-bold text-white">+127%</div>
                      <div className="text-xs text-gray-500">Avg. Traffic Boost</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-gray-500 text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown className="w-5 h-5 text-[#D4AF37]" />
        </div>
      </section>

      {/* ========== TRUSTED BY SECTION ========== */}
      <section className="py-12 bg-[#fafafa] border-b border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <span className="font-manrope text-sm text-gray-500 uppercase tracking-widest">Trusted by 500+ Brands</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
              {/* Placeholder for brand logos - using text for now */}
              {['Forbes', 'YourStory', 'Inc42', 'Business Today', 'Entrepreneur'].map((brand) => (
                <div key={brand} className="font-playfair text-lg font-bold text-gray-300 hover:text-[#D4AF37] transition-colors cursor-default">
                  {brand}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICE CATEGORIES ========== */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-medium mb-4">
              Our Services
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
              Complete PR & Media Solutions
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              From digital PR to social media promotions - everything you need to build your brand&apos;s visibility
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Digital PR */}
            <Link
              to="/services/digital-pr"
              className="group bg-white border border-[#e5e5e5] p-8 hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D4AF37]/5 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors">
                  <Newspaper className="w-7 h-7 text-[#D4AF37] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-3">Digital PR</h3>
                <p className="font-manrope text-sm text-[#737373] mb-4">
                  Get featured on top online publications, news sites, and business portals with guaranteed placements.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    High DA backlinks
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    50+ publications
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    48-hour express
                  </li>
                </ul>
                <span className="font-manrope text-sm text-[#D4AF37] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Social Media */}
            <Link
              to="/services/social-media"
              className="group bg-white border border-[#e5e5e5] p-8 hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/5 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-purple-500 transition-colors">
                  <Mic className="w-7 h-7 text-purple-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-3">Social Media</h3>
                <p className="font-manrope text-sm text-[#737373] mb-4">
                  Reach millions through verified influencers on Instagram, YouTube, and Telegram.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    100+ influencers
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    10M+ total reach
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Guaranteed views
                  </li>
                </ul>
                <span className="font-manrope text-sm text-purple-500 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>

            {/* Print Media - TEMPORARILY HIDDEN
            <Link
              to="/services/print-media"
              className="group bg-white border border-[#e5e5e5] p-8 hover:border-[#D4AF37] hover:shadow-xl transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/5 to-transparent rounded-bl-full" />
              <div className="relative">
                <div className="w-14 h-14 bg-amber-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-amber-500 transition-colors">
                  <PenTool className="w-7 h-7 text-amber-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-[#1a1a1a] mb-3">Print Media</h3>
                <p className="font-manrope text-sm text-[#737373] mb-4">
                  Premium placements in leading newspapers and magazines with verified circulation.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    50M+ daily readers
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Physical copies
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#737373]">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Regional targeting
                  </li>
                </ul>
                <span className="font-manrope text-sm text-amber-500 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
            END TEMPORARILY HIDDEN */}

            {/* Bundles */}
            <Link
              to="/services/bundles"
              className="group bg-[#0d0d0d] border border-[#0d0d0d] p-8 hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all duration-300 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full" />
              <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37] text-[#0d0d0d] text-xs font-bold group-hover:bg-[#0d0d0d] group-hover:text-white transition-colors">
                SAVE 35%
              </div>
              <div className="relative">
                <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-black/10">
                  <TrendingUp className="w-7 h-7 text-white group-hover:text-[#0d0d0d] transition-colors" />
                </div>
                <h3 className="font-playfair text-xl font-bold text-white group-hover:text-[#0d0d0d] mb-3 transition-colors">Combo Bundles</h3>
                <p className="font-manrope text-sm text-white/70 group-hover:text-[#0d0d0d]/70 mb-4 transition-colors">
                  Maximum value with curated packages combining multiple services.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-white/70 group-hover:text-[#0d0d0d]/70 transition-colors">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Multi-channel coverage
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white/70 group-hover:text-[#0d0d0d]/70 transition-colors">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Launch packages
                  </li>
                  <li className="flex items-center gap-2 text-sm text-white/70 group-hover:text-[#0d0d0d]/70 transition-colors">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Best prices
                  </li>
                </ul>
                <span className="font-manrope text-sm text-white group-hover:text-[#0d0d0d] font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ========== WHY CHOOSE US ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-medium mb-4">
              Why BusinessMint PR
            </span>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              The Complete PR Platform
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              Everything you need to build brand visibility - from digital PR to social media and print coverage.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[#fafafa] border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">Multi-Channel Coverage</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Digital PR, social media, bundles - reach your audience wherever they are.
              </p>
            </div>
            
            <div className="bg-[#fafafa] border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">100% Guaranteed</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Full refund if we can&apos;t deliver. Your investment is completely protected.
              </p>
            </div>
            
            <div className="bg-[#fafafa] border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">48-Hour Express</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Need it fast? Our express option delivers results in just 48 hours.
              </p>
            </div>
            
            <div className="bg-[#fafafa] border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">Real-Time Tracking</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Monitor every order in your dashboard with live status updates.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/marketplace"
              data-testid="view-packages-btn"
              className="btn-shimmer inline-flex items-center gap-2 px-8 py-4 font-manrope text-sm uppercase tracking-widest"
            >
              Explore All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section id="how-it-works" className="py-20 bg-[#f5f5f5] border-y border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">How It Works</h2>
            <p className="font-manrope text-[#737373]">Three simple steps to amplify your brand</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">01</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Choose Your Service</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Browse Digital PR, Social Media, or combo bundles. Select what fits your goals.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">02</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Complete Your Order</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Submit your requirements, upload content if needed, and complete secure payment.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <span className="font-mono text-2xl font-bold text-[#D4AF37]">03</span>
              </div>
              <h3 className="font-playfair text-xl font-bold mb-3 text-[#1a1a1a]">Track & Go Live</h3>
              <p className="font-manrope text-sm text-[#737373]">
                Monitor real-time status in your dashboard. Get delivery confirmation when live.
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="font-manrope text-xs text-[#a3a3a3] uppercase tracking-widest mb-2">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Real-time order tracking for all services
            </p>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-[#f5f5f5] border-y border-[#e5e5e5]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">What Our Clients Say</h2>
              <p className="font-manrope text-[#737373]">Real feedback from real customers</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((testimonial) => (
                <div key={testimonial.id} className="hover-lift bg-white border border-[#e5e5e5] p-8">
                  <Quote className="w-10 h-10 text-[#D4AF37] mb-4" />
                  <p className="font-manrope text-sm text-[#525252] mb-6 italic">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#D4AF37] text-[#0d0d0d] flex items-center justify-center font-playfair text-xl font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-manrope font-semibold text-sm text-[#1a1a1a]">{testimonial.name}</div>
                      <div className="font-manrope text-xs text-[#737373]">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link
                to="/marketplace"
                className="btn-hover inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F]"
              >
                Explore Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ========== FAQ SECTION ========== */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">Frequently Asked Questions</h2>
            <p className="font-manrope text-[#737373]">Everything you need to know about our services</p>
          </div>
          
          <div className="space-y-4">
            {displayFaqs.map((faq, index) => (
              <div key={faq.id} className="border border-[#e5e5e5] bg-[#fafafa]">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#f5f5f5] transition-colors"
                  data-testid={`faq-toggle-${index}`}
                >
                  <span className="font-manrope font-semibold text-sm pr-4 text-[#1a1a1a]">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 flex-shrink-0 text-[#D4AF37]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 flex-shrink-0 text-[#737373]" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="font-manrope text-sm text-[#737373]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 bg-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#0d0d0d]">
            Ready to Amplify Your Brand?
          </h2>
          <p className="font-manrope text-[#0d0d0d]/80 mb-8 max-w-2xl mx-auto">
            Explore our complete range of PR and media services. Digital PR, social media, print coverage, and bundled packages - all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/marketplace"
              data-testid="final-cta-btn"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#0d0d0d] text-white font-manrope text-sm uppercase tracking-widest hover:bg-black transition-all duration-300"
            >
              Explore Marketplace
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 border-2 border-[#0d0d0d] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest hover:bg-[#0d0d0d] hover:text-white transition-all duration-300"
            >
              Create Account
            </Link>
          </div>
        </div>
      </section>

      {/* ========== STICKY CTA BAR ========== */}
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ${
          showStickyBar 
            ? 'translate-y-0 opacity-100' 
            : 'translate-y-full opacity-0'
        }`}
      >
        <div className="bg-white/95 backdrop-blur-sm border-t border-[#e5e5e5] shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="hidden sm:flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#D4AF37]" />
                  <span className="font-manrope text-sm font-semibold text-[#1a1a1a]">Express Delivery Available</span>
                </div>
                <span className="text-[#e5e5e5]">|</span>
                <span className="font-manrope text-sm text-[#737373]">
                  Digital PR • Social Media • Bundles
                </span>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Link
                  to="/marketplace"
                  className="btn-hover flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest font-semibold hover:bg-[#B8960F]"
                >
                  Explore Services
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => setShowStickyBar(false)}
                  className="p-2 text-[#737373] hover:text-[#1a1a1a] transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Home;
